import React from "react";
import { Slider, Typography, Input} from "@mui/material";
import type { ShapeElement } from '../types/ShapeElement';
import { TextElement } from "../types/TextElement";
import type { Slot } from '@/types';
import { ColorPicker } from '../components/ColorPicker';



interface EffectsControlProps {
    selectedElement: { type: "slot" | "text" | "shape"; id: string };
    slots: Slot[];
    shapes: ShapeElement[];
    texts: TextElement[];
    albumType: string;
    onChange: (props: Partial<Slot | TextElement | ShapeElement>) => void;  
}

const EffectsControl: React.FC<EffectsControlProps> = ({ selectedElement, slots, shapes, texts, albumType, onChange }) => {
    let element: any;
    if (selectedElement.type === "slot") element = slots.find(s => s.id === selectedElement.id);
    if (selectedElement.type === "text") element = texts.find(t => t.id === selectedElement.id);
    if (selectedElement.type === "shape") element = shapes.find(sh => sh.id === selectedElement.id);

    if (!element) return null;


    const isImage = selectedElement?.type === "slot";
    const disabledProducts = new Set(["CANVAS", "FCANVAS", "ARCH", "MONTAGE", "FMOUNTS", "FPRINTS", "HANDTORN", "PRINTS", "HIPSTER"]);
    const isDisabled = disabledProducts.has(albumType);

    return (
        <div className="effects-control-wrapper" style={{ display: "flex", gap: 30, padding: "0px 6px", alignItems: "flex-start" }}>
            {/* Stroke Section */}

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Typography
                    sx={{
                        color: isDisabled ? "#b2b2b2" : "#5c5c5c",
                        fontFamily: "Manrope, sans-serif",
                        fontWeight: 600,        // boldness
                        fontSize: 15,            // optional
                        marginTop: -0.2,
                        marginBottom: -0.5,
                    }}
                >
                    Stroke
                </Typography>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>                 
                    <ColorPicker
                        color={isDisabled ? "#b2b2b2" : (element.strokeColor || "#000000")}
                        onChange={(val) => onChange({ strokeColor: val })}
                        disabled={isDisabled}
                    />
                    <Slider
                        value={element.strokeWidth ?? 0}
                        min={0} max={20} step={1}
                        disabled={isDisabled}
                        onChange={(_, val) => onChange({ strokeWidth: val })}
                        style={{ width: 80 , marginLeft:5}}
                        sx={{
                                height: 6,
                                width: '100%',
                                '& .MuiSlider-thumb': { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                            '& .MuiSlider-rail': { height: 5, color: "#59b3d8"},
                            '& .MuiSlider-track': { height: 4, color: "#59b3d8" },

                            }}
                    />
                    <Input
                        type="number"
                        value={element.strokeWidth ?? 0}
                        onChange={(e) => onChange({ strokeWidth: Number(e.target.value) })}
                        disabled={isDisabled}
                        disableUnderline
                        sx={{
                            width: 38,
                            height: 20,
                            borderRadius: 1,
                            fontSize: 14,
                            border: "1px solid #b2b2b2",
                            marginLeft: 0.8,

                            input: {
                                textAlign: "right",
                                paddingRight: 0.5,
                                fontSize: 14,  
                                lineHeight: "20px",        //  match height
                                boxSizing: "border-box",
                            },

                            //  override disabled styles
                            '&.Mui-disabled input': {
                                fontSize: 13,
                                lineHeight: "12px",
                                WebkitTextFillColor: "#b2b2b2", // fixes weird gray rendering in Chrome
                                opacity: 1,
                            },
                        }}
                        inputProps={{ type: "number", min: 0, max: 20 }}
                    />
                </div>
            </div>


            {/* Divider 1 */}
            <div
                style={{
                    width: 1,
                    backgroundColor: "#b2b2b2",
                    alignSelf: "stretch",
                    marginLeft: -15, // move divider closer to stroke section
                    marginRight: -15,
                    marginTop: -3,
                    // optional, adjust spacing to shadow section
                }}
            />

            {/* Shadow Section */}
            <div style={{ display: "flex", gap: 8 }}>
                {/* Column 1: Color */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" , marginRight:10}}>
                    <Typography
                        sx={{
                            color: isDisabled ? "#b2b2b2" : "#5c5c5c",     // font color
                            fontFamily: "Manrope, sans-serif",
                            fontWeight: 600,        // boldness
                            fontSize: 15, 
                            // optional
                            marginTop: -0.2,
                            marginBottom: -0.5,
                        }}
                    >
                        Shadow
                    </Typography>
                    <ColorPicker
                        color={isDisabled ? "#b2b2b2" : (element.shadowColor || "#000000")}
                        onChange={(val) => onChange({ shadowColor: val })}
                        disabled={isDisabled}
                    
                    />
                </div>

                {/* Column 2: Blur + Opacity */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 ,marginLeft:80}}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Typography
                            sx={{
                                color: isDisabled ? "#b2b2b2" : "#5c5c5c",      // font color
                                fontFamily: "Manrope, sans-serif",
                                fontWeight: 600,        // boldness
                                fontSize: 15,
                                marginLeft: -7.2,
                                marginTop:-0.5
                                
                            }}
                        >
                            Blur
                        </Typography>
                        <Slider
                            value={element.shadowBlur ?? 0}
                            min={0} max={50} step={1}
                            disabled={isDisabled}
                            onChange={(_, val) => onChange({ shadowBlur: val })}
                            style={{ width: 80, marginLeft: 10 }}
                            sx={{
                                height: 6,
                                marginTop: -0.5,
                                width: '100%',
                                '& .MuiSlider-thumb': { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                                '& .MuiSlider-rail': { height: 5, color: "#59b3d8" },
                                '& .MuiSlider-track': { height: 4, color: "#59b3d8" },

                            }}
                        />
                        <Input
                            value={element.shadowBlur ?? 0}
                            onChange={(e) => onChange({ shadowBlur: Number(e.target.value) })}
                            disabled={isDisabled}
                            disableUnderline
                            sx={{
                                width: 38,
                                height: 20,
                                borderRadius: 1,
                                fontSize: 14,
                                marginTop: -0.5,
                                border: "1px solid #b2b2b2",
                                marginLeft: 1.2,
                                input: {
                                    textAlign: "right",
                                    paddingRight: 0.5,
                                    fontSize: 14,
                                    lineHeight: "20px",        //  match height
                                    boxSizing: "border-box",
                                },

                                //  override disabled styles
                                '&.Mui-disabled input': {
                                    fontSize: 13,
                                    lineHeight: "12px",
                                    WebkitTextFillColor: "#b2b2b2", // fixes weird gray rendering in Chrome
                                    opacity: 1,
                                },
                            }}
                           
                            inputProps={{ type: "number", min: 0, max: 20 }}
                        />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Typography
                            sx={{
                                color: isDisabled ? "#b2b2b2" : "#5c5c5c",       // font color
                                fontFamily: "Manrope, sans-serif",
                                fontWeight: 600,        // boldness
                                fontSize: 15,
                                marginLeft: -11,
                                marginBottom: 1,
                            }}
                        >
                            Opacity
                        </Typography>
                        <Slider
                            value={element.shadowOpacity ?? 0}
                            min={0} max={1} step={0.01}
                            disabled={isDisabled}
                            onChange={(_, val) => onChange({ shadowOpacity: val })}
                            style={{ width: 80, marginLeft: 10 }}
                            sx={{
                                height: 6,
                                width: '100%',
                                '& .MuiSlider-thumb': { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                                '& .MuiSlider-rail': { height: 5, color: "#59b3d8" },
                                '& .MuiSlider-track': { height: 4, color: "#59b3d8" },
                                marginTop: -1,
                            }}
                        />
                        <Input
                            value={element.shadowOpacity ?? 0}
                            onChange={(e) => onChange({ shadowOpacity: Number(e.target.value) })}
                            disabled={isDisabled}
                            disableUnderline
                            sx={{
                                width: 38,
                                height: 20,
                                borderRadius: 1,
                                fontSize: 14,
                                border: "1px solid #b2b2b2",
                                marginLeft: 1.2,
                                marginBottom: 1,
                                input: {
                                    textAlign: "right",
                                    paddingRight: 0.5,
                                    fontSize: 14,
                                    lineHeight: "20px",        //  match height
                                    boxSizing: "border-box",
                                },

                                //  override disabled styles
                                '&.Mui-disabled input': {
                                    fontSize: 13,
                                    lineHeight: "12px",
                                    WebkitTextFillColor: "#b2b2b2", // fixes weird gray rendering in Chrome
                                    opacity: 1,
                                },
                            }}
                            inputProps={{ type: "number", min: 0, max: 20 }}
                        />
                    </div>
                </div>

                {/* Column 3: OffsetY + OffsetX */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Typography
                            sx={{
                                color: isDisabled ? "#b2b2b2" : "#5c5c5c",       // font color
                                fontFamily: "Manrope, sans-serif",
                                fontWeight: 600,        // boldness
                                fontSize: 15,
                                marginLeft: 2,
                                marginTop: -0.5,

                            }}
                        >
                            OffsetY
                        </Typography>
                        <Slider
                            value={element.shadowOffsetY ?? 0}
                            min={-50} max={50} step={1}
                            disabled={isDisabled}
                            onChange={(_, val) => onChange({ shadowOffsetY: val })}
                            style={{ width: 80, marginLeft: 10 }}
                            sx={{
                                height: 6,
                                marginTop: -0.5,
                                width: '100%',
                                '& .MuiSlider-thumb': { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                                '& .MuiSlider-rail': { height: 5, color: "#59b3d8" },
                                '& .MuiSlider-track': { height: 4, color: "#59b3d8" },
                            }}
                        />
                        <Input
                            value={element.shadowOffsetY ?? 0}
                            onChange={(e) => onChange({ shadowOffsetY: Number(e.target.value) })}
                            disabled={isDisabled}
                            disableUnderline
                            sx={{
                                width: 38,
                                height: 20,
                                marginTop: -0.5,
                                borderRadius: 1,
                                fontSize: 14,
                                border: "1px solid #b2b2b2",
                                marginLeft: 1.2,
                                input: {
                                    textAlign: "right",
                                    paddingRight: 0.5,
                                    fontSize: 14,
                                    lineHeight: "20px",        //  match height
                                    boxSizing: "border-box",
                                },

                                //  override disabled styles
                                '&.Mui-disabled input': {
                                    fontSize: 13,
                                    lineHeight: "12px",
                                    WebkitTextFillColor: "#b2b2b2", // fixes weird gray rendering in Chrome
                                    opacity: 1,
                                },
                            }}
                            inputProps={{ type: "number", min: 0, max: 20 }}
                        />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <Typography
                            sx={{
                                color: isDisabled ? "#b2b2b2" : "#5c5c5c",      // font color
                                fontFamily: "Manrope, sans-serif",
                                fontWeight: 600,        // boldness
                                fontSize: 15,
                                marginLeft: 2,
                                marginBottom: 1,
                            }}
                        >
                            OffsetX
                        </Typography>
                        <Slider
                            value={element.shadowOffsetX ?? 0}
                            min={-50} max={50} step={1}
                            disabled={isDisabled}
                            onChange={(_, val) => onChange({ shadowOffsetX: val })}
                            style={{ width: 80, marginLeft: 10 }}
                            sx={{
                                height: 6,
                                width: '100%',
                                '& .MuiSlider-thumb': { width: 16, height: 16, backgroundColor: "#fff", border: 1 },
                                '& .MuiSlider-rail': { height: 5, color: "#59b3d8" },
                                '& .MuiSlider-track': { height: 4, color: "#59b3d8" },
                                marginTop: -1,
                            }}
                        />
                        <Input
                            value={element.shadowOffsetX ?? 0}
                            onChange={(e) => onChange({ shadowOffsetX: Number(e.target.value) })}
                            disabled={isDisabled}
                            disableUnderline
                            sx={{
                                width: 38,
                                height: 20,
                                borderRadius: 1,
                                fontSize: 14,
                                border: "1px solid #b2b2b2",
                                marginLeft: 1.2,
                                marginTop: -1,
                                input: {
                                    textAlign: "right",
                                    paddingRight: 0.5,
                                    fontSize: 14,
                                    lineHeight: "20px",        //  match height
                                    boxSizing: "border-box",
                                },

                                //  override disabled styles
                                '&.Mui-disabled input': {
                                    fontSize: 13,
                                    lineHeight: "12px",
                                    WebkitTextFillColor: "#b2b2b2", // fixes weird gray rendering in Chrome
                                    opacity: 1,
                                },
                            }}
                            inputProps={{ type: "number", min: 0, max: 20 }}
                        />
                    </div>
                </div>
            </div>

            {/* Divider 2 */}
            <div
                style={{
                    width: 1,
                    backgroundColor: "#b2b2b2",
                    alignSelf: "stretch",
                    marginLeft: -15, // move divider closer to stroke section
                    marginRight: -15   // optional, adjust spacing to shadow section
                }}
            />

            {/* Adjustments Section */}
            <div style={{ display: "flex", gap: 16 }}>
              
                {/* Column 2: Saturation + B&W */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                   
                    {/* Custom Image Buttons */}
                    <div style={{ display: "flex", gap: 8 }}>
                        {/* B&W Button */}
                        <Typography
                            sx={{
                                color: isImage ? "#5c5c5c" : "#999",  // 
                                fontFamily: "Manrope, sans-serif",
                                fontWeight: 600,        // boldness
                                fontSize: 15,
                                marginLeft: 1.5,
                               // marginTop: 0.1,
                                //marginBottom: 1,
                            }}
                        >
                            Grayscale
                        </Typography>
                        <button
                            onClick={() => onChange({ blackWhite: !element.blackWhite })}
                            disabled={!isImage}
                            style={{
                                width: 28,
                                height: 28,
                                border: element.blackWhite ? "2px solid #59b3d8" : "1px solid #b2b2b2",
                                borderRadius: 6,
                                padding: 0,
                                marginTop:-3,
                                background: isImage ? "transparent" : "#f5f5f5",
                                cursor: isImage ? "pointer" : "not-allowed",
                                opacity: isImage ? 1 : 0.5, // fade button
                            }}
                        >
                            <img
                                src={`${process.env.PUBLIC_URL}/grayscale.png`}
                                alt="B&W"
                                style={{ width: "100%", height: "100%" }}
                            />
                        </button>
                      
                    </div>

                </div>
            </div>
        </div>
    );
};

export default EffectsControl;
